import React, { JSX, useCallback, useEffect } from "react";
import useStripeLink from "../hooks/useStripeLink";
import useHubspotLink from "../hooks/useHubspotLink";
import useSendInviteEmail from "../hooks/useSendInviteEmail";
import useUpdateClientDetails from "../hooks/useUpdateClientDetails";
import useReplaceMainUserForClient from "../hooks/useReplaceMainUserForClient";
import useAddInvestmentForm from "../hooks/useAddInvestmentForm";
import { useRequest } from "../../../hooks/useRequest";
import { ClientDetailsContext } from "./clientDetailsContext";
import routes from "../../../routes";

export const ClientDetailsProvider: ({
  clientId,
  children,
}: {
  clientId: string;
  children: React.ReactElement;
}) => JSX.Element = ({
  clientId,
  children,
}: {
  clientId: string;
  children: React.ReactElement;
}) => {
  const { fetch, response, error, loading } = useRequest();

  const linkToStripe = useStripeLink(response?.data.mainUser.id);
  const linkToHubspot = useHubspotLink(response?.data.mainUser.id);
  const sendInviteEmail = useSendInviteEmail(response?.data.mainUser.id);
  const updateClientDetails = useUpdateClientDetails(clientId);
  const replaceMainUser = useReplaceMainUserForClient(clientId);
  const addInvestment = useAddInvestmentForm(clientId);

  const fetchClientDetails = useCallback(() => {
    fetch("GET", routes.api.client.individual({ id: clientId }));
  }, [fetch, clientId]);

  const isEditClientSuccess = useCallback(
    () =>
      (updateClientDetails.alert &&
        updateClientDetails.alert.type !== "error") ||
      (replaceMainUser.alert && replaceMainUser.alert.type !== "error") ||
      (addInvestment.alert && addInvestment.alert.type !== "error"),
    [updateClientDetails.alert, replaceMainUser.alert, addInvestment.alert],
  );

  useEffect(() => {
    fetchClientDetails();
  }, [
    clientId,
    replaceMainUser.mainUser?.id,
    linkToHubspot.url,
    linkToStripe.url,
    fetchClientDetails,
  ]);

  useEffect(() => {
    if (isEditClientSuccess()) {
      fetchClientDetails();
    }
  }, [
    clientId,
    updateClientDetails.alert?.type,
    replaceMainUser.alert?.type,
    addInvestment.alert,
    addInvestment.alert?.type,
    isEditClientSuccess,
    fetchClientDetails,
  ]);

  return (
    <ClientDetailsContext.Provider
      value={{
        linkToHubspot,
        linkToStripe,
        sendInviteEmail,
        replaceMainUser,
        updateClientDetails,
        addInvestment,
        client: {
          loading,
          error,
          details: response?.data,
        },
        clientId,
      }}
    >
      {children}
    </ClientDetailsContext.Provider>
  );
};

export default ClientDetailsProvider;
